import { queueApplyPreset, queueApplyStyle } from "@/store/processing/image-processing"
import { store } from "@/store/store"
import { getImageInfo, updateImageInfo } from "../processing/image-info"
import { PhotoRecord, StyleInfoRecord } from "@/lib/catalog"
import { ClusterThreshold, CullingInfoRecord, StyleTaskRecord } from "@/lib/catalog/types"
import { hasNoEmbeddedPreview } from "@/lib/catalog/utils"
import { hasManualChanges } from "../store-history"
import { currentStyleAtom } from "./style-state"
import { PresetItem } from "@/store/presets/presets-service"
import { analytics } from "@/telemetry/amplitude"
import { isCullingModeAtom, projectAtom } from "../project/projects-state"
import {
	currentPhotoAtom,
	sortedPhotosAtom,
	photosAtom,
	cullingInfoAtom,
	getCullingInfo,
	processablePhotosAtom,
	updateCullingInfos,
	allPhotosAtom,
	updatePhotos,
	getPhotoMetadata,
} from "../photo/photos-state"
import { isPerformanceDebugEnabled } from "@/utils/measurement/feature-flag"
import {
	beginTaskPerformanceMeasurement,
	commitTaskPerformanceMeasurement,
} from "../debug/task-performance-state"
import { processingQueue } from "../processing/processing-queue"
import {
	finishAIStyleRunIfIdle,
	setAIStyleWaitingForPhotos,
} from "../processing/ai-style-performance"
import { editClusteringImporter } from "../importing/edit-clustering-importing"
import { PhotoId } from "../undoable-state"
import {
	Clusters,
	generateCullingClusters,
	getSceneDist,
	getSortedPhotosAndIndexMap,
} from "@/lib/ai/ai-cluster"
import { atom } from "jotai"
import { getVisibleThumbsProcessingPriorities } from "../processing/processing-queue-priority"
import { popDialog, replaceDialog } from "../dialogs-state"
import { ClusteringProgressDialog } from "@/components/Dialogs/ClusteringProgressDialog"
import { getApplyAIStyleProperties } from "./style-event-utils"
import { logClustering } from "@/lib/ai/utils"

const CLUSTERING_NEIGHBORHOOD_SIZE = 35

/**
 * Check if a photo has no embedded preview (can't be pre-clustered for edit mode)
 * @param photoId - The ID of the photo to check
 * @returns True if the photo has no embedded preview, false otherwise
 */
function hasNoPreview(photoId: PhotoId): boolean {
	const metadata = getPhotoMetadata(photoId)
	return hasNoEmbeddedPreview(metadata)
}

/**
 * Check if a photo's clustering neighborhood is complete for edit clustering.
 * Requires 30 photos before and after the cluster to have edit clustering info.
 */
function isClusteringNeighborhoodComplete(
	allCullingInfos: Record<string, CullingInfoRecord>,
	memberIds: string[],
): boolean {
	// only check processable photos (visible and not unsupported)
	const { sortedPhotos, idToIndex } = getSortedPhotosAndIndexMap(store.get(processablePhotosAtom))
	memberIds.sort((a, b) => (idToIndex[a] ?? 0) - (idToIndex[b] ?? 0))

	const id1 = memberIds[0] ?? ""
	const index1 = idToIndex[id1] ?? 0
	const id2 = memberIds[memberIds.length - 1] ?? ""
	const index2 = idToIndex[id2] ?? 0

	for (
		let i = Math.max(0, index1 - CLUSTERING_NEIGHBORHOOD_SIZE);
		i <= Math.min(index2 + CLUSTERING_NEIGHBORHOOD_SIZE, sortedPhotos.length - 1);
		i++
	) {
		const cullingInfo = allCullingInfos[sortedPhotos[i]?.id ?? ""]
		if (hasNoPreview(sortedPhotos[i]?.id ?? "")) continue
		if (!cullingInfo) return false
		if (!cullingInfo.clusterLeaderIdMap?.[ClusterThreshold.EditClustering]) return false
	}

	return true
}

/**
 * Determine whether a photo is ready to have a style applied immediately.
 *
 * A photo is "ready" when we are confident its key photo (cluster leader) has been
 * identified and all photos in its ±35-photo neighbourhood have been clustered.
 * This matters we want to ensure deterministic key photo assignment regardless of
 * the order of processing.
 *
 * Three fast-path exits (photo is immediately ready):
 * 1. imageInfo.keyNeighborId is already set — the AI pipeline already ran for this photo.
 * 2. Photo has no embedded preview (e.g. some DNGs) — can't pre-cluster; falls back to
 *    on-the-fly key photo resolution inside applyStyle() which is not deterministic.
 * 3. Clustering neighbourhood is complete — safe to proceed.
 */
function isPhotoReadyForStyle(
	photoId: PhotoId,
	processableCullingInfos: Record<string, CullingInfoRecord>,
	clusters: Clusters,
): boolean {
	const imageInfo = getImageInfo(photoId)
	if (imageInfo?.keyNeighborId) {
		return true
	}
	// Photos without an embedded preview can't be pre-clustered in time; applyStyle()
	// will resolve their key photo on the fly using the old method.
	if (hasNoPreview(photoId)) {
		return true
	}
	const cullingInfo = getCullingInfo(photoId)
	if (!cullingInfo) {
		return false
	}
	const keyNeighborId = cullingInfo.clusterLeaderIdMap?.[ClusterThreshold.EditClustering]
	if (!keyNeighborId) {
		return false
	}
	const memberIds = clusters[keyNeighborId]
	if (!memberIds) {
		return false
	}

	return isClusteringNeighborhoodComplete(processableCullingInfos, memberIds)
}

// Make sure already processed photos (style preview photos / references during setup) are the key photos in pre-clustering
function setPrioritizedKeyPhotos(options: {
	readyPhotoIds: PhotoId[]
	style: StyleInfoRecord
	clusters: Clusters
}) {
	const { readyPhotoIds, style, clusters } = options

	const prioritizeImageIds = [...(style.prioritizeImageIds ?? [])]
	const photos = store.get(photosAtom)
	for (const photoId of readyPhotoIds) {
		const photo = photos[photoId]
		if (photo?.kind === "reference") {
			prioritizeImageIds.push(photoId)
		}
	}

	const keyMapping: Record<string, string[]> = {}
	for (const id of prioritizeImageIds) {
		const cullingInfo = getCullingInfo(id)
		if (!cullingInfo) {
			continue
		}
		const keyNeighborId = cullingInfo.clusterLeaderIdMap?.[ClusterThreshold.EditClustering]
		if (!keyNeighborId) {
			continue
		}
		if (!keyMapping[keyNeighborId]) {
			keyMapping[keyNeighborId] = []
		}
		keyMapping[keyNeighborId]?.push(id)
	}

	const idToNewKey: Record<string, string> = {}
	for (const keyNeighborId in keyMapping) {
		const newKeyIds = keyMapping[keyNeighborId]
		const memberIds = clusters[keyNeighborId]
		if (!memberIds || !newKeyIds || newKeyIds.length === 0) {
			continue
		}
		if (newKeyIds.length === 1) {
			for (const memberId of memberIds) {
				idToNewKey[memberId] = newKeyIds[0] as string
			}
		} else {
			// split cluster into multiple clusters
			logClustering(
				"[setPrioritizedKeyPhotos] the cluster contains multiple prioritized photos",
				{ newKeyIds, memberIds, prioritizeImageIds },
			)
			const { idToIndex } = getSortedPhotosAndIndexMap(store.get(allPhotosAtom))
			const sortedNewKeyIds = newKeyIds.sort(
				(a, b) => (idToIndex[a] ?? 0) - (idToIndex[b] ?? 0),
			)
			const sortedMemberIds = memberIds.sort(
				(a, b) => (idToIndex[a] ?? 0) - (idToIndex[b] ?? 0),
			)
			let currKeyIndex = 0
			for (const memberId of sortedMemberIds) {
				const memberSortedIndex = idToIndex[memberId] ?? 0
				const currKeyId = sortedNewKeyIds[currKeyIndex] as string
				const currKeySortedIndex = idToIndex[currKeyId] ?? 0
				if (memberSortedIndex <= currKeySortedIndex) {
					idToNewKey[memberId] = currKeyId
					continue
				}

				const nextKeyIndex = currKeyIndex + 1
				if (nextKeyIndex < sortedNewKeyIds.length) {
					const nextKeyId = sortedNewKeyIds[nextKeyIndex] as string
					const nextKeySortedIndex = idToIndex[nextKeyId] ?? 0
					if (memberSortedIndex >= nextKeySortedIndex) {
						currKeyIndex = nextKeyIndex
					} else {
						const cullingInfo = getCullingInfo(memberId) as CullingInfoRecord
						if (!cullingInfo) {
							continue
						}
						const currKeyCullingInfo = getCullingInfo(
							sortedNewKeyIds[currKeyIndex] as string,
						)
						if (!currKeyCullingInfo) {
							continue
						}
						const nextKeyCullingInfo = getCullingInfo(
							sortedNewKeyIds[nextKeyIndex] as string,
						)
						if (!nextKeyCullingInfo) {
							continue
						}
						const distToCurrKey = getSceneDist(cullingInfo, currKeyCullingInfo)
						const distToNextKey = getSceneDist(cullingInfo, nextKeyCullingInfo)
						if (distToCurrKey > distToNextKey) {
							currKeyIndex = nextKeyIndex
						}
					}
				}

				idToNewKey[memberId] = sortedNewKeyIds[currKeyIndex] as string
			}
		}
	}

	for (const photoId of prioritizeImageIds) {
		const newKeyId = idToNewKey[photoId]
		if (newKeyId && newKeyId !== photoId) {
			console.error(
				"[setPrioritizedKeyPhotos] Prioritized photo is not the key photo. PhotoId:",
				photoId,
				"NewKeyId:",
				newKeyId,
			)
		}
	}

	const allCullingInfos = store.get(cullingInfoAtom)
	updateCullingInfos((changes) => {
		for (const photoId of readyPhotoIds) {
			const cullingInfo = allCullingInfos[photoId]
			if (!cullingInfo || !cullingInfo.clusterLeaderIdMap) {
				continue
			}

			const oldKey = cullingInfo.clusterLeaderIdMap[ClusterThreshold.EditClustering]
			const newKey = idToNewKey[photoId]
			const alreadyFinal =
				cullingInfo.clusterLeaderIdMap[ClusterThreshold.EditClusteringFinal]

			// Skip if already finalized and no key change needed
			if (alreadyFinal && (!newKey || newKey === oldKey)) {
				continue
			}

			// Mark as final for ALL ready photos so mergeClusters won't overwrite
			const finalKey = newKey || oldKey
			changes[photoId] = {
				...cullingInfo,
				clusterLeaderIdMap: {
					...cullingInfo.clusterLeaderIdMap,
					// Update EditClustering if there's a new key
					...(newKey &&
						newKey !== oldKey && { [ClusterThreshold.EditClustering]: newKey }),
					// Set EditClusteringFinal to lock the value
					...(finalKey && { [ClusterThreshold.EditClusteringFinal]: finalKey }),
				},
			}
		}
	}, true)
}

/**
 * Derived atom that builds the edit-clustering cluster map from cullingInfoAtom.
 * Jotai recomputes this automatically whenever cullingInfoAtom or processablePhotosAtom
 * changes, so callers always get an up-to-date view without manual invalidation.
 *
 * Only processable photos (visible, not unsupported formats) are included so that
 * hidden/rejected photos don't pollute the cluster topology.
 */
const editingClustersAtom = atom((get) => {
	try {
		const allCullingInfos = get(cullingInfoAtom)
		const processablePhotos = get(processablePhotosAtom)
		const processablePhotoIds = new Set(processablePhotos.map((photo) => photo.id))
		const processableCullingInfos: Record<string, CullingInfoRecord> = Object.fromEntries(
			Object.entries(allCullingInfos).filter(([photoId]) => processablePhotoIds.has(photoId)),
		)
		return {
			processablePhotoIds,
			clusters: generateCullingClusters(
				processableCullingInfos,
				ClusterThreshold.EditClustering,
			),
		}
	} catch (error) {
		console.error("[editingClustersAtom] Error computing clusters:", error)
		throw error
	}
})

// Track pending style application
interface PendingStyleApplication {
	styleId: string
	pendingPhotoIds: Set<PhotoId>
	isReapply: boolean
	triggerSource?: string | null
}

let currentPendingApplication: PendingStyleApplication | null = null

/**
 * Clean up pending style application state
 * Note: Photo status is managed by processingQueue since we set currentTask for all photos
 */
export function cleanupPendingStyleApplication() {
	currentPendingApplication = null
	setAIStyleWaitingForPhotos(false)
}

/**
 * Check pending photos and queue them when their clustering neighborhood is complete
 * This is called when:
 * 1. A photo is clustered (clustering data added to cullingInfoAtom)
 * 2. A photo is marked as having no preview (can't be clustered)
 *
 * The editingClustersAtom provides automatic caching - it only recomputes when
 * cullingInfoAtom or processablePhotosAtom changes.
 */
function checkAndQueuePendingPhotos(style: StyleInfoRecord) {
	if (!currentPendingApplication || currentPendingApplication.styleId !== style.styleId) {
		return
	}

	const photos = store.get(photosAtom)
	const allCullingInfos = store.get(cullingInfoAtom)

	// Use cached/memoized clusters (automatically recomputes when dependencies change)
	const { processablePhotoIds, clusters } = store.get(editingClustersAtom)

	const readyPhotoIds: PhotoId[] = []

	// Check which pending photos are now ready
	for (const photoId of currentPendingApplication.pendingPhotoIds) {
		// directly queue photos that are not processable (applyStyle will does nothing for them)
		if (
			!processablePhotoIds.has(photoId) ||
			isPhotoReadyForStyle(photoId, allCullingInfos, clusters)
		) {
			readyPhotoIds.push(photoId)
		}
	}

	if (readyPhotoIds.length > 0) {
		setPrioritizedKeyPhotos({
			readyPhotoIds,
			style,
			clusters,
		})

		// Update priorities to ensure key photos are processed before members
		const priorities = getVisibleThumbsProcessingPriorities()

		for (const prioritizedId of style.prioritizeImageIds ?? []) {
			if (hasNoPreview(prioritizedId)) {
				priorities.set(prioritizedId, 10_000_000)
			}
		}
		processingQueue.setTaskPriorities(priorities)

		// Queue the ready photos
		for (const photoId of readyPhotoIds) {
			const photo = photos[photoId]
			if (photo) {
				queueApplyStyle(photo, style, currentPendingApplication.triggerSource)
				currentPendingApplication.pendingPhotoIds.delete(photoId)
			}
		}

		logClustering(
			`[checkAndQueuePendingPhotos] Queued ${readyPhotoIds.length} newly-clustered photos. ${currentPendingApplication.pendingPhotoIds.size} remaining.`,
		)
	}

	// Check if all photos are queued
	if (currentPendingApplication.pendingPhotoIds.size === 0) {
		const isReapply = currentPendingApplication.isReapply
		logClustering(
			`[checkAndQueuePendingPhotos] All photos queued — style application complete.`,
		)
		cleanupPendingStyleApplication()
		trackBatchApplyComplete(style, isReapply)
	} else {
		// Safeguard: if editClusteringImporter has already finished but some photos never
		// received clustering results (e.g. decode errors, unsupported formats), queue them
		// anyway so they don't hang in "pending" state forever. applyStyle() will fall back
		// to on-the-fly clustering for these photos.
		if (
			!editClusteringImporter.isInProgress &&
			currentPendingApplication.pendingPhotoIds.size > 0
		) {
			const remainingPhotoIds = Array.from(currentPendingApplication.pendingPhotoIds)
			console.warn(
				`[checkAndQueuePendingPhotos] Safeguard triggered: clustering finished but ${remainingPhotoIds.length} photos never received results. Forcing queueApplyStyle.`,
				remainingPhotoIds.map((id) => ({ id, cullingInfo: getCullingInfo(id) })),
			)
			for (const photoId of remainingPhotoIds) {
				const photo = photos[photoId]
				if (photo) {
					queueApplyStyle(photo, style, currentPendingApplication.triggerSource)
					currentPendingApplication.pendingPhotoIds.delete(photoId)
				}
			}
			if (currentPendingApplication.pendingPhotoIds.size === 0) {
				const isReapply = currentPendingApplication.isReapply
				logClustering(`[checkAndQueuePendingPhotos] All photos queued via safeguard.`)
				cleanupPendingStyleApplication()
				trackBatchApplyComplete(style, isReapply)
			}
		}
	}
}

/**
 * Track batch apply completion analytics
 */
function trackBatchApplyComplete(style: StyleInfoRecord, isReapply = false) {
	finishAIStyleRunIfIdle()
	analytics.trackEvent({
		label: "apply_ai_style",
		customProperties: {
			styleId: style.styleId,
			styleName: style.name,
			styleVersion: style.version,
			projectId: store.get(projectAtom)?.id,
			reason: "batch",
			...getApplyAIStyleProperties(style, isReapply),
		},
		type: "event",
	})
}

export function applyStyleToAllPhotos(
	style: StyleInfoRecord,
	forceReapply = false,
	triggerSource?: string | null,
) {
	const allPhotos = store.get(sortedPhotosAtom)
	const photosToProcess = forceReapply
		? allPhotos
		: store.get(sortedPhotosAtom).filter((photo) => {
				return (
					photo.kind == "default" &&
					(photo.styleId != style.styleId || photo.styleVersion != style.version)
				)
			})

	if (isPerformanceDebugEnabled()) {
		beginTaskPerformanceMeasurement("ai-style-apply")

		processingQueue.queueEvents.subscribe((event) => {
			if (event !== "updateQueue") {
				return
			}

			const { remainingTasks } = processingQueue

			if (!remainingTasks) {
				commitTaskPerformanceMeasurement("ai-style-apply")
			}
		})
	}

	batchApplyStyle(photosToProcess, style, forceReapply, triggerSource)
}

// Pre-clustering progress state for the blocking modal
export const preClusteringProgressAtom = atom<{
	clusteredCount: number
	totalCount: number
} | null>(null)

/**
 * Apply style to photos with a blocking modal that shows clustering progress.
 * User can wait for clustering to complete (so visibility-priority photos are processed immediately) or skip (clustering will run in the background and may block visible photos processing until clustering finishes for that photo).
 *
 * @param photos - Photos to apply style to
 * @param style - Style to apply
 * @param prioritizeImageIds - Optional photo IDs to prioritize as key photos during clustering
 */
export function applyStyleWithPreClustering(
	photos: PhotoRecord[],
	style: StyleInfoRecord,
	prioritizeImageIds?: PhotoId[],
	onComplete?: () => void,
	triggerSource?: string | null,
) {
	if (photos.length === 0) {
		onComplete?.()
		return
	}

	const project = store.get(projectAtom)
	if (!project) {
		onComplete?.()
		return
	}

	const photoIds = photos.map((p) => p.id)
	const totalCount = photos.length

	// Count how many photos already have edit clustering
	const allCullingInfos = store.get(cullingInfoAtom)
	let clusteredCount = 0
	for (const photoId of photoIds) {
		const cullingInfo = allCullingInfos[photoId]
		const imageInfo = getImageInfo(photoId)
		if (
			cullingInfo?.clusterLeaderIdMap?.[ClusterThreshold.EditClustering] ||
			hasNoPreview(photoId) ||
			imageInfo?.keyNeighborId
		) {
			clusteredCount++
		}
	}

	// If all photos are already clustered, proceed directly without showing the modal
	if (clusteredCount >= totalCount) {
		logClustering(
			`[applyStyleWithPreClustering] All ${totalCount} photos already clustered — skipping modal.`,
		)
		batchApplyStyle(photos, style, false, triggerSource)
		onComplete?.()
		return
	}

	logClustering(
		`[applyStyleWithPreClustering] Starting pre-clustering modal: ${clusteredCount}/${totalCount} photos ready.`,
	)

	// Track if dialog has been dismissed (skip or complete)
	let dialogDismissed = false

	// Handler to close dialog and proceed
	const proceedWithBatchApply = (allPhotosClustered: boolean) => {
		if (dialogDismissed) return
		dialogDismissed = true

		// Cleanup
		store.set(preClusteringProgressAtom, null)
		if (allPhotosClustered) {
			editClusteringImporter.cancel()
		} else {
			// use clearTasks() to preserve culler model and photosWithoutPreview cache
			editClusteringImporter.clearTasks()
		}
		popDialog()

		// Proceed with batch apply: will handle remaining on-demand clustering if needed
		batchApplyStyle(photos, style, false, triggerSource)

		// Notify caller that pre-clustering phase is complete
		onComplete?.()
	}

	// Handler for skip button
	const handleSkip = () => {
		proceedWithBatchApply(false)
	}

	// Initialize progress state
	store.set(preClusteringProgressAtom, { clusteredCount, totalCount })

	// Show the blocking modal once (dialog reads progress from atom)
	replaceDialog(ClusteringProgressDialog, {
		onSkip: handleSkip,
	})

	// Progress update function - called by editClusteringImporter callback
	// This handles both clustered photos AND photos without preview (e.g., DNGs)
	const updateProgress = () => {
		if (dialogDismissed) return

		const currentCullingInfos = store.get(cullingInfoAtom)
		let newClusteredCount = 0

		for (const photoId of photoIds) {
			const cullingInfo = currentCullingInfos[photoId]
			const imageInfo = getImageInfo(photoId)
			if (
				cullingInfo?.clusterLeaderIdMap?.[ClusterThreshold.EditClustering] ||
				hasNoPreview(photoId) ||
				imageInfo?.keyNeighborId
			) {
				newClusteredCount++
			}
		}

		// Update progress in atom (dialog reads from this atom directly)
		store.set(preClusteringProgressAtom, { clusteredCount: newClusteredCount, totalCount })

		// Check if all done
		if (newClusteredCount >= totalCount) {
			proceedWithBatchApply(true)
			return
		}

		// If clustering finished but some photos never got clustered (e.g. errors),
		// dismiss the dialog and fall back to on-demand clustering for those photos.
		if (!editClusteringImporter.isInProgress) {
			console.warn(
				"[applyStyleWithPreClustering] Pre-clustering finished with missing photos; falling back to on-demand clustering.",
			)
			proceedWithBatchApply(false)
		}
	}

	// Start the editClusteringImporter with callback for progress updates
	// The callback is invoked both when:
	// 1. A batch of photos is clustered (cullingInfoAtom updated)
	// 2. A photo is marked as having no preview (e.g., DNGs - photosWithoutPreview updated)
	void editClusteringImporter.start(project, photoIds, prioritizeImageIds ?? [], updateProgress)
}

/**
 * Apply style to all photos with pre-clustering progress modal.
 * Wraps applyStyleToAllPhotos with the blocking pre-clustering modal.
 */
export function applyStyleToAllPhotosWithPreClustering(
	style: StyleInfoRecord,
	prioritizeImageIds?: PhotoId[],
	triggerSource?: string | null,
) {
	const photosToProcess = store.get(sortedPhotosAtom).filter((photo) => {
		return photo.kind == "default" && photo.styleId != style.styleId && !photo.isHidden
	})

	if (isPerformanceDebugEnabled()) {
		beginTaskPerformanceMeasurement("ai-style-apply")

		processingQueue.queueEvents.subscribe((event) => {
			if (event !== "updateQueue") {
				return
			}

			const { remainingTasks } = processingQueue

			if (!remainingTasks) {
				commitTaskPerformanceMeasurement("ai-style-apply")
			}
		})
	}

	applyStyleWithPreClustering(
		photosToProcess,
		style,
		prioritizeImageIds,
		undefined,
		triggerSource,
	)
}

/**
 * Core entry point for applying a style to a batch of photos.
 *
 * Flow:
 * 1. Split photos into "ready" (clustering neighbourhood complete) and "pending".
 * 2. Queue ready photos immediately; mark pending photos so they show a spinner.
 * 3. Start editClusteringImporter for the full batch. As each photo's neighbourhood
 *    becomes complete, checkAndQueuePendingPhotos() is called to queue it.
 *
 * Important: setPrioritizedKeyPhotos() is called before queuing to ensure that
 * reference photos (or any style.prioritizeImageIds) become the cluster leader
 * for their group, so their known adjustments propagate to members correctly.
 */
export function batchApplyStyle(
	photos: PhotoRecord[],
	style: StyleInfoRecord,
	isReapply = false,
	triggerSource?: string | null,
) {
	if (photos.length == 0) {
		return
	}

	// Clean up any previous pending application
	cleanupPendingStyleApplication()

	const project = store.get(projectAtom)
	if (!project) return

	const readyPhotos: PhotoRecord[] = []
	const pendingPhotoIds = new Set<PhotoId>()
	const allCullingInfos = store.get(cullingInfoAtom)
	const { processablePhotoIds, clusters } = store.get(editingClustersAtom)

	// Split: photos whose ±35-neighbourhood is fully clustered go straight to the
	// processing queue; the rest wait for editClusteringImporter to finish their area.
	for (const photo of photos) {
		if (
			!processablePhotoIds.has(photo.id) ||
			isPhotoReadyForStyle(photo.id, allCullingInfos, clusters)
		) {
			readyPhotos.push(photo)
		} else {
			pendingPhotoIds.add(photo.id)
		}
	}

	logClustering(
		`[batchApplyStyle] Ready: ${readyPhotos.length}, Pending: ${pendingPhotoIds.size}`,
	)

	// Mark not-ready photos as pending with apply-style currentTask immediately; ready photos will be handled by queueApplyStyle.
	// This ensures:
	// 1. Photos show pending overlay for better UX
	// 2. On app reopen, pending photos will be resumed (not just cleared)
	// However, resumed photos which don't have pre-edit clustering results will use the old on-the-fly clustering method.
	const taskRecord: StyleTaskRecord = { type: "apply-style", styleId: style.styleId }
	void updatePhotos((photoUpdates) => {
		for (const photoId of pendingPhotoIds) {
			photoUpdates[photoId] = {
				status: "pending",
				currentTask: taskRecord,
			}
		}
	})

	setPrioritizedKeyPhotos({
		readyPhotoIds: readyPhotos.map((p) => p.id),
		style,
		clusters,
	})

	// Update priorities to ensure key photos are processed before members
	const priorities = getVisibleThumbsProcessingPriorities()
	// But if the prioritized photos have no preview, meaning they can't be set as key photos currently, set their priority to a highest value
	for (const prioritizedId of style.prioritizeImageIds ?? []) {
		if (hasNoPreview(prioritizedId)) {
			priorities.set(prioritizedId, 10_000_000)
		}
	}
	processingQueue.setTaskPriorities(priorities)

	// Queue photos that are ready immediately
	setAIStyleWaitingForPhotos(pendingPhotoIds.size > 0)
	for (const photo of readyPhotos) {
		queueApplyStyle(photo, style, triggerSource)
	}

	if (pendingPhotoIds.size === 0) {
		// All done immediately!
		trackBatchApplyComplete(style, isReapply)
		return
	}

	// Schedule clustering for ALL photos passed to batchApplyStyle
	// editClusteringImporter will handle ordering internally (filename → timestamp when available)
	// Photos that already have clustering will be skipped during extraction (standard pattern)
	const photoIds = photos.map((p) => p.id)

	logClustering(`[batchApplyStyle] Scheduling pre-edit clustering for ${photoIds.length} photos.`)

	// Set up pending application state
	currentPendingApplication = {
		styleId: style.styleId,
		pendingPhotoIds,
		isReapply,
		triggerSource,
	}

	// Start clustering with callback that checks for ready photos after each clustering completes
	// This callback is called both when:
	// 1. A batch of photos are successfully clustered (cullingInfoAtom updated)
	// 2. A photo is marked as having no preview (photosWithoutPreview set updated)
	void editClusteringImporter.start(project, photoIds, style.prioritizeImageIds ?? [], () => {
		checkAndQueuePendingPhotos(style)
	})
}

export function batchApplyPreset(
	photos: PhotoRecord[],
	preset: PresetItem,
	strength = 1,
	allowStrengthAdjustment = false,
	onCompletionOnCurrentPhoto?: () => void,
) {
	const currentPhoto = store.get(currentPhotoAtom)
	for (const photo of photos) {
		const isCurrentPhoto = currentPhoto?.id === photo.id
		queueApplyPreset(
			photo,
			preset,
			strength,
			allowStrengthAdjustment,
			isCurrentPhoto ? onCompletionOnCurrentPhoto : undefined,
		)
	}
}

export async function processPhotoIfNeeded(photo: PhotoRecord, triggerSource?: string | null) {
	if (store.get(isCullingModeAtom)) {
		return
	}

	const style = store.get(currentStyleAtom)
	if (style) {
		const imageInfo = getImageInfo(photo.id)

		let shouldProcess = false
		let isReapply = false
		if (!imageInfo?.aiStyleAdjustments) {
			shouldProcess = true // should do initial processing
		} else if (imageInfo?.needApplyStyle) {
			shouldProcess = true // should do reprocessing
			isReapply = true
		} else {
			// no ai updates, but may need to override user adjustments
			const hasUserChanges = await hasManualChanges(photo)
			if (hasUserChanges) {
				const updatedInfo = { ...imageInfo, needApplyStyle: true }
				updateImageInfo(updatedInfo)
				shouldProcess = true
				isReapply = true
			}
		}

		if (shouldProcess) {
			queueApplyStyle(photo, style, triggerSource)

			const values = {
				count: 1,
				styleId: style.styleId,
				styleName: style.name,
				projectId: store.get(projectAtom)?.id,
				reason: "action_triggered",
				...getApplyAIStyleProperties(style, isReapply),
			}

			analytics.trackEvent({
				label: "apply_ai_style",
				customProperties: values,
				type: "event",
			})
		}
	}
}
